'use client';

import type { RouterEvent } from 'appRouterInstance';
import _ from 'lodash';
import {
  AppRouterContext,
  type AppRouterInstance,
} from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useContext, useEffect } from 'react';
import type { ChildrenProps } from 'viewup-lib/common';

const routerCoreMethod = ['push', 'replace', 'refresh', 'back', 'forward'] as const;

const routerEvents: Record<RouterEvent, Array<(url: string, options?: any) => void>> = {
  routeChangeComplete: [],
  routeChangeStart: [],
};

const events: AppRouterInstance['events'] = {
  off(event: RouterEvent, cb: (url: string, options?: any) => void) {
    routerEvents[event] = _.without(routerEvents[event], cb);
  },
  on(event: RouterEvent, cb: (url: string, options?: any) => void) {
    routerEvents[event] = [...routerEvents[event], cb];
  },
};

function proxy(router: AppRouterInstance, routerField: (typeof routerCoreMethod)[number]) {
  const descriptor = Object.getOwnPropertyDescriptor(router, routerField);
  if (
    descriptor?.get &&
    (descriptor.get as { __viewupPatched?: boolean }).__viewupPatched === true
  ) {
    return;
  }

  const method = router[routerField];

  const getter = () => {
    return async (url: string, options?: any) => {
      try {
        if (!_.isEmpty(routerEvents.routeChangeStart)) {
          const promiseList = routerEvents.routeChangeStart.map((cb) => cb(url, options));
          await Promise.all(promiseList);
        }

        method(url, options);
      } catch (e) {
        console.error('proxy', e);
      }
    };
  };
  (getter as { __viewupPatched?: boolean }).__viewupPatched = true;

  Object.defineProperty(router, routerField, {
    get: getter,
    configurable: true,
  });
}

export const RouteDetectConsumer = (props: ChildrenProps) => {
  const router = useContext(AppRouterContext);

  useEffect(() => {
    if (!router) return;
    router.events = events;
    routerCoreMethod.forEach((field) => {
      proxy(router, field);
    });
  }, [router]);

  return <>{props.children}</>;
};
